// Guided tour (#160): dims the app and spotlights one real element at a time,
// with a short card, Back / Next and Skip. Opt-in only, started from Help
// ("Show me around"); single-screen first-visit tips stay as they are.
//
// It moves between tabs to reach a step but never acts on its own: no
// generating, no starting a workout. Steps name their target with a
// data-tour attribute, and tourSteps.test.js checks the attributes exist.

import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "../ui/button";
import { AppStateContext } from "../../contexts/WorkoutContext";
import { TOURS } from "../../help/tours";
import { onStartTour, markTourSeen } from "../../utils/tourBus";

const PAD = 8; // spotlight padding around the target, px
const WAIT_MS = 3000; // how long to wait for a step's element to appear

// The first visible element for a step (desktop and phone navs both exist).
const findTarget = (id) =>
  [...document.querySelectorAll(`[data-tour="${id}"]`)].find((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }) || null;

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export default function Tour() {
  const { setView } = useContext(AppStateContext);
  const [tour, setTour] = useState(null); // { id, steps }
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const cardRef = useRef(null);
  const step = tour?.steps[index] || null;

  const end = useCallback(() => {
    if (tour) markTourSeen(tour.id);
    setTour(null);
    setIndex(0);
    setRect(null);
  }, [tour]);

  useEffect(
    () =>
      onStartTour((id) => {
        const found = TOURS[id];
        if (!found) return;
        setTour({ id, steps: found.steps });
        setIndex(0);
      }),
    [],
  );

  // Go to the step's tab, then wait for its element and keep the spotlight on it.
  useEffect(() => {
    if (!step) return undefined;
    if (step.view) setView(step.view);
    setRect(null);
    let raf = 0;
    let target = null;
    const started = Date.now();
    const measure = () => {
      target = target && document.contains(target) ? target : findTarget(step.target);
      if (target) {
        if (!measure.scrolled) {
          target.scrollIntoView({ block: "center", behavior: reducedMotion() ? "auto" : "smooth" });
          measure.scrolled = true;
        }
        const r = target.getBoundingClientRect();
        setRect((prev) =>
          prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height
            ? prev
            : { top: r.top, left: r.left, width: r.width, height: r.height },
        );
      } else if (Date.now() - started > WAIT_MS) {
        setRect("missing");
        return;
      }
      raf = requestAnimationFrame(measure);
    };
    measure.scrolled = false;
    raf = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(raf);
  }, [step, setView]);

  // Focus the card for keyboards and screen readers; Esc skips.
  useLayoutEffect(() => {
    if (step) cardRef.current?.focus();
  }, [step]);
  useEffect(() => {
    if (!tour) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") end();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tour, end]);

  if (!tour || !step) return null;

  const last = index === tour.steps.length - 1;
  const box = rect && rect !== "missing" ? rect : null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const hole = box && {
    x: Math.max(0, box.left - PAD),
    y: Math.max(0, box.top - PAD),
    w: Math.min(vw, box.width + PAD * 2),
    h: box.height + PAD * 2,
  };
  // The card sits away from the spotlight: below it when it's in the top
  // half, above it otherwise; at the bottom (thumb reach) when there's none.
  const cardBelow = hole ? hole.y + hole.h / 2 < vh / 2 : true;
  const cardStyle = hole
    ? cardBelow
      ? { top: Math.min(hole.y + hole.h + 12, vh - 220) }
      : { bottom: Math.min(vh - hole.y + 12, vh - 220) }
    : { bottom: 24 };
  const motion = reducedMotion() ? "" : "transition-all duration-300 ease-out";

  return (
    <div className="fixed inset-0 z-[120]" role="presentation">
      <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {hole && <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx="14" fill="black" className={motion} />}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#tour-mask)" />
        {hole && (
          <rect
            x={hole.x}
            y={hole.y}
            width={hole.w}
            height={hole.h}
            rx="14"
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            className={motion}
          />
        )}
      </svg>
      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label={step.title}
        tabIndex={-1}
        className={`absolute inset-x-4 mx-auto max-w-sm rounded-2xl border border-border bg-card p-4 shadow-xl outline-none ${motion}`}
        style={cardStyle}
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-xs font-medium text-muted-foreground">{`${index + 1} of ${tour.steps.length}`}</span>
          <button type="button" onClick={end} aria-label="Skip tour" className="rounded-full p-1 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h2 className="text-base font-semibold text-foreground">{step.title}</h2>
        <p className="mt-1 text-sm leading-relaxed text-foreground/85">{step.body}</p>
        <div className="mt-4 flex items-center gap-2">
          <Button type="button" variant="ghost" onClick={end} className="text-muted-foreground">
            Skip
          </Button>
          <div className="flex-1" />
          {index > 0 && (
            <Button type="button" variant="outline" onClick={() => setIndex((i) => i - 1)}>
              Back
            </Button>
          )}
          <Button type="button" onClick={() => (last ? end() : setIndex((i) => i + 1))}>
            {last ? "Done" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}
